import React from "react";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { ActivityBadge, ReferenceBadge, HourType } from "./Badges";

interface Project {
  id: string;
  activityId: number;
  name: string;
  activity: string;
  client: string;
  project: string;
  reference: string;
  hourType: HourType;
}

interface ProjectSelectorProps {
  value: string;
  onChange: (projectId: string) => void;
  projects: Project[];
  disabled?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  value,
  onChange,
  projects,
  disabled = false,
  error,
  label = "Project & Activity",
  placeholder = "Select a project",
  required = true,
  className = "",
}) => {
  // Format project options for dropdown
  const projectOptions = projects.map((project) => ({
    label: `${project.client} • ${project.project} (${project.activity})`,
    value: project.id,
    project: project, // Include the full project object for templates
  }));

  // Custom item template for dropdown options
  const itemTemplate = (option: any) => {
    if (!option.project) return option.label;

    return (
      <div className="flex align-items-center gap-2">
        <ReferenceBadge value={`${option.project.reference}`} />
        {option.project.client} • {option.project.project}
        <ActivityBadge
          value={option.project.activity}
          hourType={option.project.hourType}
        />
      </div>
    );
  };

  // Custom value template for selected value display
  const valueTemplate = (option: any) => {
    if (!option) {
      // Return a div with consistent height when no selection
      return <div style={{ minHeight: "1.5rem" }}></div>;
    }

    const selectedProject = projects.find(
      (p) => p.id === option.value || p.id === option
    );
    if (!selectedProject) return option.label || option;

    return (
      <div className="flex align-items-center gap-2">
        <ReferenceBadge value={`${selectedProject.reference}`} />
        {selectedProject.client} • {selectedProject.project}
        <ActivityBadge
          value={selectedProject.activity}
          hourType={selectedProject.hourType}
        />
      </div>
    );
  };

  return (
    <div className="field">
      <FloatLabel>
        <Dropdown
          id="project"
          value={value}
          onChange={(e) => onChange(e.value)}
          options={projectOptions}
          placeholder={placeholder}
          filter
          disabled={disabled}
          className={`w-full ${error ? "p-invalid" : ""} ${className}`}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
          filterInputAutoFocus
        />
        <label htmlFor="project" className="block text-sm font-medium mb-2">
          {label} {required && "*"}
        </label>
      </FloatLabel>
      {error && <small className="p-error">{error}</small>}
    </div>
  );
};
